import React, { useEffect, useState, type JSX } from "react";
import Head from "next/head";
import { MarketingMenu } from "../../components/MarketingMenu";
import { MarketingOGTags } from "../../components/MarketingOGTags";
import { FadeUpContainer } from "../../components/ReusableStyledComponents/FadeUp";
import { MarketingTextBlock } from "../../components/MarketingTextBlock";
import { VerticalSpacing } from "../../components/VerticalSpacing";
import { MarketingCTA, CTASize } from "../../components/MarketingCTA";
import {
    HomePageVariantCentered,
    HomepageVariantCustomH4,
    HomepageVariantCustomH2,
    HomepageBlockBackgroundWrapper,
    HomePageVariantMasonryCut,
    HomePageVariantMasonryWrapper,
    HomepageAuthoryTeamImage
} from "./HomepagePageLayout.styles";
import { MarketingTextSVGColumn } from "../../components/MarketingTextSVGColumn";
import { HPMainBtnWrapper, HPMainFloatWrapper } from "./PortfolioPageLayout.styles";
import { InlineLink } from "../../components/ReusableStyledComponents/InlineLink";
import { MarketingG2Banner } from "../../components/MarketingG2Banner";
import { MarketingFooter } from "../../components/MarketingFooter";
import { MarketingTextSVGHeader, PricingDesktop } from "../../components/MarketingTextSVGHeader";
import { UnstyledLink } from "../../components/ReusableStyledComponents/UnstyledLink";
import { APPLICATION_ROUTES } from "../../shared-logic/routes";
import { theme } from "../../theme/theme";
import { MarketingIconsBlock, MarketingIconBlock } from "../../components/MarketingIconsBlock";
import { SVGGeneric } from "../../components/ReusableStyledComponents/SVGGeneric";
import { MarketingGetStarted } from "../../components/MarketingGetStarted";
import dynamic from "next/dynamic";
import { MarketingAllBenefits } from "../../components/MarketingAllBenefits";
import { useWindowSize } from "../../hooks/useWindowSize";
import { UserResponse, ExampleUserListResponse } from "@authory-packages/core";
import { SEO_URL_EXAMPLE_PAGES_SUFIX } from "@authory-packages/seo-static-data";
import { EmbedMode, EmbedTheme } from "../../utils/embeds";
import { Masonry } from "masonic";
import { EmbedMasonCardWrapper, EmbedProfileWrapper } from "../examples/ExamplesPageLayout.styles";
import { V3EmbedProfile } from "../../components/V3EmbedProfile";
import { MarketingTitleBr } from "../../components/ReusableStyledComponents/MarketingMainTitle";
import { MarketingFAQ } from "../../components/MarketingFAQ";
import { Body } from "../../components/Typography";
import { LandingPageDataMock } from "../../mocks/landing_page_data";
import { HomePageDataProps } from "../../utils/homepage_data/homepage_data";
import { generateFAQSchema } from "../../utils/faqSchemaGenerator";

function formatSentence(sentence: string) {
    // Trim to remove leading/trailing spaces
    sentence = sentence.trim();

    // Capitalize the first character
    sentence = sentence.charAt(0).toUpperCase() + sentence.slice(1);

    // Add period if sentence doesn't end with punctuation
    if (!/[.!?]$/.test(sentence)) {
        sentence += ".";
    }

    return sentence;
}

function newLineToBr(text: string) {
    const lines = text.split("\n");

    return lines.map((line, i) => {
        if (i === 0) {
            return <React.Fragment key={i}>{line}</React.Fragment>;
        } else {
            return (
                <React.Fragment key={i}>
                    <br />
                    {line}
                </React.Fragment>
            );
        }
    });
}

function convertAsterisksToLinks(text: string, key: string) {
    return text.replace(/\*\*(.*?)\*\*/g, (_, p1) => {
        const url = `${process.env.NEXT_PUBLIC_FRONTEND_URL}/examples/${key}${SEO_URL_EXAMPLE_PAGES_SUFIX}`;
        return `<a href="${url}">${p1}</a>`;
    });
}

export const LANDING_PAGES_DOMAINS_SEO = [
    {
        url: `${APPLICATION_ROUTES.USE_CASES_PROFESSION}`,
        label: "By profession"
    },
    {
        url: `${APPLICATION_ROUTES.USE_CASES_PROFESSION_CATEGORIES}`,
        label: "By professional categories"
    }
];

const MarketingCarouselUser = dynamic(() => import("../../components/MarketingCarouselUser/MarketingCarouselUser"), { ssr: false });

type HomepagePageLayoutProps = {
    customTitle?: JSX.Element;
    userVariant?: boolean;
    data: HomePageDataProps | LandingPageDataMock;
    usersList: ExampleUserListResponse;
    isLandingPage?: boolean;
    canonical: string;
};

export const HomepagePageLayout = ({ data, usersList, isLandingPage = false, canonical }: HomepagePageLayoutProps) => {
    const size = useWindowSize();
    const [useSSR, setUseSSR] = useState(false);

    const IS_MOBILE = !!size.width && size.width <= theme.v3.layout.sidebarLayout.mobile;

    useEffect(() => {
        setUseSSR(true);
    }, []);

    const embedList = usersList.users.map((item) => {
        return {
            embedTheme: EmbedTheme.light,
            embedMode: Math.random() > 0.15 ? EmbedMode.normal : EmbedMode.compact,
            showSocialMedia: Math.random() > 0.15,
            showHighlight: Math.random() > 0.15,
            useCover: true,
            items: [],
            author: {
                ...(item.user as UserResponse)
            }
        };
    });

    let jsonLd = "";

    if (data.faq) {
        jsonLd = generateFAQSchema(data.faq);
    }

    return (
        <>
            <Head>{!!jsonLd.length && <script type="application/ld+json">{jsonLd}</script>}</Head>
            <MarketingOGTags title={data.ogTitle} description={data.ogDescription} canonicalUrl={canonical} />
            <MarketingMenu />
            <FadeUpContainer>
                <HomepageBlockBackgroundWrapper>
                    <div>
                        <VerticalSpacing top={-30}>
                            <MarketingTextSVGHeader
                                customTitleMarginBottom={20}
                                textMarginTop={30}
                                title={
                                    <>
                                        <strong>{newLineToBr(data.title)}</strong>
                                    </>
                                }
                                description={<>{data.subtitle}</>}
                                desktopSVG={
                                    <svg width="1351" height="224" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M75 168a4 4 0 0 1-4-4v-40a4 4 0 0 1 4-4h44v44a4 4 0 0 1-4 4H75Z" fill="#82AEFF" />
                                        <path d="M119.5 79.5H.5V4A3.5 3.5 0 0 1 4 .5h115.5v79Z" stroke="#E4EDFF" />
                                        <path
                                            d="M179 80.5h-59.5V140a3.5 3.5 0 0 0 3.5 3.5h59.5V84a3.5 3.5 0 0 0-3.5-3.5Z"
                                            stroke="#E4EDFF"
                                            strokeMiterlimit="10"
                                        />
                                        <path
                                            d="m142.782 91.287-5.254 3.227a1.946 1.946 0 0 0-.64 2.674l18.167 29.677c.226.369.549.669.935.868l6.037 3.115a1.354 1.354 0 0 0 1.973-1.213l-.034-6.803a2.376 2.376 0 0 0-.349-1.228L145.45 91.927a1.939 1.939 0 0 0-2.668-.64Z"
                                            fill="#E4EDFF"
                                        />
                                        <path d="M1239 176h48v44a4 4 0 0 1-4 4h-40a4 4 0 0 1-4-4v-44Z" fill="#FFA858" />
                                        <path
                                            d="M1287.5 72.5h63V172c0 1.933-1.57 3.5-3.5 3.5h-59.5v-103ZM1195 128.5h40c1.93 0 3.5 1.567 3.5 3.5v43.5H1195c-1.93 0-3.5-1.567-3.5-3.5v-40c0-1.933 1.57-3.5 3.5-3.5Z"
                                            stroke="#E4EDFF"
                                        />
                                    </svg>
                                }
                                mobileSVG={<></>}
                                textMaxWidth={1200}
                                containerMaxWidth={1320}
                                mobileMaxWidth={344}
                                descriptionTextMaxWidth={700}
                                floatingComponent={
                                    <HPMainFloatWrapper userVariant={false}>
                                        <HPMainBtnWrapper>
                                            <VerticalSpacing top={0} breakpoints={[{ screen: theme.breakpoints.tablet, marginTop: -20 }]}>
                                                <UnstyledLink href={APPLICATION_ROUTES.SIGNUP}>
                                                    <MarketingCTA
                                                        text="Create your portfolio in 5 minutes"
                                                        size={!IS_MOBILE ? CTASize.L : CTASize.M}
                                                    />
                                                </UnstyledLink>
                                            </VerticalSpacing>
                                        </HPMainBtnWrapper>
                                        <VerticalSpacing top={-33}>
                                            <MarketingAllBenefits useBlockVariant lazy={false} />
                                        </VerticalSpacing>
                                    </HPMainFloatWrapper>
                                }
                            />
                        </VerticalSpacing>
                        <VerticalSpacing top={20} breakpoints={[{ screen: theme.breakpoints.tablet, marginTop: 150 }]}>
                            <MarketingTextBlock
                                blockMaxWidth={820}
                                backgroundColor="white"
                                title={
                                    <>
                                        <HomepageVariantCustomH2>{data.first_section_title}</HomepageVariantCustomH2>
                                    </>
                                }
                                description={
                                    <>
                                        {data.first_section_description.split("\n").map((pr) => (
                                            <React.Fragment key={pr}>
                                                {pr}
                                                <br />
                                            </React.Fragment>
                                        ))}
                                    </>
                                }
                            />
                        </VerticalSpacing>
                    </div>
                    <div>
                        <HomePageVariantCentered>
                            <HomepageVariantCustomH4>
                                <strong>{data.first_benefit_title}</strong>
                            </HomepageVariantCustomH4>
                        </HomePageVariantCentered>
                        <VerticalSpacing top={30}>
                            <MarketingTextSVGColumn
                                homepageVariant
                                title={<HomepageVariantCustomH2 dangerouslySetInnerHTML={{ __html: data.first_benefit_point }} />}
                                description={<>{formatSentence(data.first_benefit_description)}</>}
                                svgPosition="left"
                                desktopImgWidth={471}
                                desktopImgHeight={333}
                                desktopImage={"/authory_static_files/images/homepage/sliders/features/protect_desktop.png"}
                                mobileImage={"/authory_static_files/images/homepage/sliders/features/protect_desktop.png"}
                                mobileMaxWidth={378}
                                snapBr={theme.breakpoints.desktop}
                            />
                        </VerticalSpacing>
                        <VerticalSpacing top={60}>
                            <MarketingTextSVGColumn
                                homepageVariant
                                title={<HomepageVariantCustomH2 dangerouslySetInnerHTML={{ __html: data.first_benefit_point_2 }} />}
                                description={<>{formatSentence(data.first_benefit_description_2)}</>}
                                svgPosition="left"
                                desktopImgWidth={471}
                                desktopImgHeight={333}
                                desktopImage={"/authory_static_files/images/features/present/types_desktop.svg"}
                                mobileImage={"/authory_static_files/images/features/present/types_mobile.png"}
                                mobileMaxWidth={378}
                                snapBr={theme.breakpoints.desktop}
                            />
                        </VerticalSpacing>
                    </div>
                    <div>
                        <HomePageVariantCentered>
                            <HomepageVariantCustomH4>
                                <strong>{data.second_benefit_title}</strong>
                            </HomepageVariantCustomH4>
                        </HomePageVariantCentered>
                        <VerticalSpacing top={30}>
                            <MarketingTextSVGColumn
                                homepageVariant
                                title={<HomepageVariantCustomH2 dangerouslySetInnerHTML={{ __html: data.second_benefit_point }} />}
                                description={
                                    <VerticalSpacing bottom={0} breakpoints={[{ screen: 1100, marginBottom: 60 }]}>
                                        {formatSentence(data.second_benefit_description)}
                                    </VerticalSpacing>
                                }
                                desktopImgWidth={471}
                                desktopImgHeight={333}
                                desktopImage={"/authory_static_files/images/homepage/sliders/features/present_desktop.png"}
                                mobileImage={"/authory_static_files/images/homepage/sliders/features/present_desktop.png"}
                                mobileMaxWidth={378}
                                snapBr={theme.breakpoints.desktop}
                            />
                        </VerticalSpacing>
                        <VerticalSpacing top={60}>
                            <MarketingTextSVGColumn
                                homepageVariant
                                title={<HomepageVariantCustomH2 dangerouslySetInnerHTML={{ __html: data.second_benefit_point_2 }} />}
                                description={<>{formatSentence(data.second_benefit_description_2)}</>}
                                desktopImgWidth={471}
                                desktopImgHeight={333}
                                desktopImage={"/authory_static_files/images/features/curate/sharing_desktop.png"}
                                mobileImage={"/authory_static_files/images/features/curate/sharing_mobile.png"}
                                mobileMaxWidth={378}
                                snapBr={theme.breakpoints.desktop}
                            />
                        </VerticalSpacing>
                    </div>
                    <div>
                        <HomePageVariantCentered>
                            <HomepageVariantCustomH4>
                                <strong>{data.third_benefit_title}</strong>
                            </HomepageVariantCustomH4>
                        </HomePageVariantCentered>
                        <VerticalSpacing top={30}>
                            <MarketingTextSVGColumn
                                homepageVariant
                                title={<HomepageVariantCustomH2 dangerouslySetInnerHTML={{ __html: data.third_benefit_point }} />}
                                description={<>{formatSentence(data.third_benefit_description)}</>}
                                desktopImgWidth={471}
                                desktopImgHeight={333}
                                desktopImage={"/authory_static_files/images/homepage/sliders/features/track_desktop.png"}
                                mobileImage={"/authory_static_files/images/homepage/sliders/features/track_desktop.png"}
                                mobileMaxWidth={378}
                                svgPosition="left"
                                snapBr={theme.breakpoints.desktop}
                            />
                        </VerticalSpacing>
                        <VerticalSpacing top={60}>
                            <MarketingTextSVGColumn
                                homepageVariant
                                title={<HomepageVariantCustomH2 dangerouslySetInnerHTML={{ __html: data.third_benefit_point_2 }} />}
                                description={<>{formatSentence(data.third_benefit_description_2)}</>}
                                desktopImgWidth={471}
                                desktopImgHeight={333}
                                desktopImage={"/authory_static_files/images/homepage/sliders/features/distribute_desktop.png"}
                                mobileImage={"/authory_static_files/images/homepage/sliders/features/distribute_desktop.png"}
                                mobileMaxWidth={378}
                                svgPosition="left"
                                snapBr={theme.breakpoints.desktop}
                            />
                        </VerticalSpacing>
                    </div>
                    <div>
                        <HomePageVariantCentered>
                            <HomepageVariantCustomH4>
                                <strong>{data.fourth_benefit_title}</strong>
                            </HomepageVariantCustomH4>
                        </HomePageVariantCentered>
                        <VerticalSpacing top={30}>
                            <MarketingTextSVGColumn
                                homepageVariant
                                title={<HomepageVariantCustomH2 dangerouslySetInnerHTML={{ __html: data.fourth_benefit_point }} />}
                                description={<>{formatSentence(data.fourth_benefit_description)}</>}
                                desktopImgWidth={524}
                                desktopImgHeight={370}
                                desktopImage={"/authory_static_files/images/homepage/sliders/features/analyze_desktop.png"}
                                mobileImage={"/authory_static_files/images/homepage/sliders/features/analyze_desktop.png"}
                                mobileMaxWidth={378}
                                snapBr={theme.breakpoints.desktop}
                            />
                        </VerticalSpacing>
                        <VerticalSpacing top={60}>
                            <MarketingTextSVGColumn
                                homepageVariant
                                title={<HomepageVariantCustomH2 dangerouslySetInnerHTML={{ __html: data.fourth_benefit_point_2 }} />}
                                description={<>{formatSentence(data.fourth_benefit_description_2)}</>}
                                desktopImgWidth={524}
                                desktopImgHeight={370}
                                desktopImage={"/authory_static_files/images/features/curate/automated_desktop.png"}
                                mobileImage={"/authory_static_files/images/features/curate/automated_mobile.png"}
                                mobileMaxWidth={378}
                                snapBr={theme.breakpoints.desktop}
                            />
                        </VerticalSpacing>
                    </div>
                    {!isLandingPage && (
                        <div>
                            <MarketingCarouselUser
                                homepageVariant
                                text={
                                    <>
                                        {data.userCarouselTitle ? (
                                            data.userCarouselTitle
                                        ) : (
                                            <>
                                                <strong>Join the thousands of content creators</strong> who trust Authory to back up and showcase
                                                their work
                                            </>
                                        )}
                                    </>
                                }
                            />
                        </div>
                    )}
                    <div>
                        {!!data.faq.length ? (
                            <VerticalSpacing top={120}>
                                <MarketingFAQ questions={data.faq} />
                            </VerticalSpacing>
                        ) : (
                            <>
                                <VerticalSpacing bottom={-40}>
                                    <HomePageVariantCentered>
                                        <HomepageVariantCustomH2>
                                            <strong>We keep your content safe.</strong> That's a promise.
                                        </HomepageVariantCustomH2>
                                    </HomePageVariantCentered>
                                </VerticalSpacing>
                                <MarketingTextBlock
                                    noPadding
                                    backgroundColor="white"
                                    blockMaxWidth={1800}
                                    title={<></>}
                                    description={
                                        <>
                                            <VerticalSpacing top={40}>
                                                <p>Our only goal at Authory is to help you and your audience. That's why...</p>
                                            </VerticalSpacing>
                                            <VerticalSpacing top={80}>
                                                <MarketingIconsBlock
                                                    useDash={false}
                                                    icons={[
                                                        <MarketingIconBlock
                                                            title={
                                                                <>
                                                                    <strong>Ad- and AI-free,</strong> forever
                                                                </>
                                                            }
                                                            text={
                                                                <>
                                                                    Authory doesn't sell your data, neither to PR firms nor to AI companies.
                                                                    <br /> <br />
                                                                    Your data is safe with us. No hidden agendas here.
                                                                </>
                                                            }
                                                            icon={
                                                                <SVGGeneric
                                                                    loading="lazy"
                                                                    src="/authory_static_files/images/icons/devices_portfolio.svg"
                                                                />
                                                            }
                                                            minHeight={134}
                                                        />,
                                                        <MarketingIconBlock
                                                            title={
                                                                <>
                                                                    <strong>Copyrights?</strong> Check
                                                                </>
                                                            }
                                                            text={
                                                                <>
                                                                    All content added to your Authory account is treated as private (it's 100%
                                                                    copyright compliant), and you're the only one who can access the full-text backup.
                                                                    We will not republish your content.
                                                                </>
                                                            }
                                                            icon={
                                                                <SVGGeneric loading="lazy" src="/authory_static_files/images/icons/copyrights.svg" />
                                                            }
                                                            minHeight={134}
                                                        />,
                                                        <MarketingIconBlock
                                                            title={
                                                                <>
                                                                    <strong>No lock in,</strong> ever
                                                                </>
                                                            }
                                                            text={
                                                                <>
                                                                    We don't lock you in. You can export your entire archive of content at any time
                                                                    and we'll send it to you as an XML or HTML file.
                                                                </>
                                                            }
                                                            icon={
                                                                <SVGGeneric loading="lazy" src="/authory_static_files/images/icons/no_lock_in.svg" />
                                                            }
                                                            minHeight={134}
                                                        />
                                                    ]}
                                                />
                                            </VerticalSpacing>
                                            <VerticalSpacing top={40} breakpoints={[{ screen: theme.breakpoints.desktop, marginTop: 0 }]}>
                                                <p>
                                                    Read more about copyright and ownership{" "}
                                                    <InlineLink href={APPLICATION_ROUTES.OWNERSHIP}>here</InlineLink>.
                                                </p>
                                            </VerticalSpacing>
                                        </>
                                    }
                                    textAlignment="center"
                                />
                            </>
                        )}
                    </div>
                    {isLandingPage ? (
                        <div>
                            <HomePageVariantCentered>
                                <HomepageVariantCustomH2>
                                    <strong>{data.example_pages_reference_title}</strong>
                                </HomepageVariantCustomH2>
                                <VerticalSpacing top={25} bottom={60}>
                                    <Body
                                        dangerouslySetInnerHTML={{
                                            __html: convertAsterisksToLinks(data.example_pages_reference_description, "key" in data ? data.key : "")
                                        }}
                                    />
                                </VerticalSpacing>
                            </HomePageVariantCentered>
                            <VerticalSpacing top={80} bottom={40}>
                                <HomePageVariantMasonryWrapper>
                                    <HomePageVariantMasonryCut>
                                        <EmbedProfileWrapper>
                                            {!!embedList.length && useSSR && (
                                                <Masonry
                                                    items={embedList}
                                                    render={({ index, data }) => (
                                                        <EmbedMasonCardWrapper key={index}>
                                                            <V3EmbedProfile
                                                                author={data.author}
                                                                embedMode={data.embedMode}
                                                                embedTheme={data.embedTheme}
                                                                items={data.items}
                                                                showSocialMedia={data.showSocialMedia}
                                                                showHighlight={data.showHighlight}
                                                                useCover={data.useCover}
                                                                numBioLines={4}
                                                            />
                                                        </EmbedMasonCardWrapper>
                                                    )}
                                                    columnGutter={20}
                                                    columnWidth={280}
                                                />
                                            )}
                                        </EmbedProfileWrapper>
                                    </HomePageVariantMasonryCut>
                                </HomePageVariantMasonryWrapper>
                            </VerticalSpacing>
                        </div>
                    ) : (
                        <div>
                            <HomePageVariantCentered>
                                <HomepageVariantCustomH2 style={{ textAlign: "center" }}>
                                    <strong>We're for the writers, the creators, the storytellers;</strong>
                                    <MarketingTitleBr /> for those creating something worth saving.
                                </HomepageVariantCustomH2>
                            </HomePageVariantCentered>
                            <VerticalSpacing top={-10} breakpoints={[{ screen: theme.breakpoints.tablet, marginTop: 40 }]}>
                                <MarketingTextSVGHeader
                                    floatingComponent={
                                        <HomepageAuthoryTeamImage src="/authory_static_files/images/authory_team.jpg" loading="lazy" />
                                    }
                                    customTitleMarginBottom={0}
                                    desktopSVG={<PricingDesktop />}
                                    mobileSVG={<></>}
                                    textMaxWidth={970}
                                    description={<></>}
                                    containerMaxWidth={1320}
                                    mobileMaxWidth={343}
                                />
                            </VerticalSpacing>
                            <VerticalSpacing top={45} breakpoints={[{ screen: theme.breakpoints.tablet, marginTop: -10 }]}>
                                <MarketingTextBlock
                                    blockMaxWidth={820}
                                    description={
                                        <>
                                            <p>
                                                {data.pledge_description_first_paragraph ? (
                                                    <>{data.pledge_description_first_paragraph}</>
                                                ) : (
                                                    <>
                                                        We know that as a writer and content creator, your work doesn't end with the final draft.
                                                        There's working with your team to get it live, reviewing analytics, pitching new projects. Not
                                                        to mention trying to stay afloat when websites go dark, publication cadence increases, and
                                                        some people just can't tell the difference between automated text and great writing.
                                                    </>
                                                )}
                                            </p>
                                            <VerticalSpacing top={30}>
                                                <p>
                                                    {data.pledge_description_second_paragraph ? (
                                                        <>{data.pledge_description_second_paragraph}</>
                                                    ) : (
                                                        <>
                                                            That's why we built Authory. So you can more easily find, back up, browse, showcase, and
                                                            share all of the work you've ever done.
                                                        </>
                                                    )}
                                                </p>
                                            </VerticalSpacing>
                                        </>
                                    }
                                    textAlignment="center"
                                    noPadding
                                />
                            </VerticalSpacing>
                        </div>
                    )}
                    {!isLandingPage && (
                        <div>
                            <MarketingG2Banner />
                            <VerticalSpacing top={30}>
                                <MarketingGetStarted
                                    ctaLabel="Create your account in 5 minutes"
                                    showG2Banner={false}
                                    description="Create your portfolio now"
                                />
                            </VerticalSpacing>
                        </div>
                    )}
                </HomepageBlockBackgroundWrapper>
                <VerticalSpacing top={60}>
                    <MarketingFooter />
                </VerticalSpacing>
            </FadeUpContainer>
        </>
    );
};
